<template>
  <div class="wrapper-preview-container" :class="{ 'has-avatar': !isDefaultAvatar }">
    <div class="wrapper-preview-container" v-bind:style="{ height: previewCardHeight + 'px' }">
      <div class="wrapper-preview" ref="previewCardWrapper" v-bind:style="{ transform: 'scale(' + previewCardScale + ')' }">
        <card-content
          ref="previewCard"
          :type="type"
          :name="name"
          :date="date"
          :custom-title="customTitle"
          :top-src="topImageData"
          :bottom-src="bottomImageData"
          :avatar-src="avatarImageData" />

        <div class="form__file" v-if="editing">
          <input type="file" accept="image/*" @change="onSelectFile('avatar', $event.target)" ref="file" />
        </div>

        <div class="form__file--bg" v-if="editing">
          <input type="file" accept="image/*" @change="onSelectFile('top', $event.target)" ref="file" />
        </div>

        <div v-if="editing && (topImageSrc.startsWith('http') || this.files.top)" class="remove-file">
          <i class="fas fa-trash-alt"></i><a href="#" @click.prevent="removeTopImage">&nbsp;ลบภาพ</a>
        </div>
      </div>

      <div class="preview-edit">
        <a href="#" @click.prevent="edit" class="pull-right" v-if="showEditLink && !editMode">แก้ไข</a>
        <a href="#" @click.prevent="cancel" class="pull-right" v-if="showEditLink && editMode">ยกเลิก</a>
      </div>
    </div>

    <div class="capture-image-wrapper" v-if="editing">
      <div class="capture-image" ref="captureCard">
        <card-content
          ref="capturePageCard"
          :type="type"
          :name="name"
          :date="date"
          :custom-title="customTitle"
          :top-src="topImageData"
          :bottom-src="bottomImageData"
          :avatar-src="avatarImageData" />
      </div>

      <div class="capture-canvas" ref="captureCanvas"></div>
    </div>
  </div>
</template>

<script>
  import CardContent from './card-content'
  import html2canvas from 'html2canvas'

  const Pica = require('../../../../../node_modules/pica/dist/pica.min')

  /**
   * @see: https://github.com/brion/min-wasm-fail/blob/master/min-wasm-fail.js
   * @see: https://support.scandit.com/hc/en-us/articles/115003947711-Known-issues-with-mobile-Safari-on-iOS-11-2-2-and-11-2-5
   */
  function testSafariWebAssemblyBug () {
    let bin = new Uint8Array([0, 97, 115, 109, 1, 0, 0, 0, 1, 6, 1, 96, 1, 127, 1, 127, 3, 2, 1, 0, 5, 3, 1, 0, 1, 7, 8, 1, 4, 116, 101, 115, 116, 0, 0, 10, 16, 1, 14, 0, 32, 0, 65, 1, 54, 2, 0, 32, 0, 40, 2, 0, 11])
    let mod = new window.WebAssembly.Module(bin)
    let inst = new window.WebAssembly.Instance(mod, {})

    // test storing to and loading from a non-zero location via a parameter.
    // Safari on iOS 11.2.5 returns 0 unexpectedly at non-zero locations
    return (inst.exports.test(4) !== 0)
  }

  let pica = null
  if (window.WebAssembly && testSafariWebAssemblyBug()) {
    pica = Pica({ features: [ 'js', 'wasm', 'ww', 'cib' ] })
  } else {
    pica = Pica({ features: [ 'js', 'cib', 'ww' ] })
  }

  const UPLOAD_RETRY_LIMIT = 3
  const UPLOAD_RETRY_WAIT = 2500
  const UPLOAD_MAX_SIZE = 2000000

  const DEFAULT_AVATAR_URL = '/static/img/image-preset-event/default-avatar.jpg'

  export default {
    name: 'SpecialEventCard',

    components: {
      CardContent
    },

    props: {
      type: {
        type: Number,
        default: 1
      },
      name: {
        type: String,
        default: ''
      },
      date: {
        type: String,
        default: ''
      },
      customTitle: {
        type: String,
        default: ''
      },
      avatarImage: {
        type: String,
        default: ''
      },
      topImage: {
        type: String,
        default: ''
      },
      bottomImage: {
        type: String,
        default: ''
      },
      page: {
        type: Object,
        default: null
      },
      editing: {
        type: Boolean,
        default: false
      }
    },

    computed: {
      title () {
        return this.type
      },

      showEditLink () {
        return this.$store.state.token && this.page && this.$store.state.user && this.$store.state.user.uuid === this.page.member_id
      },

      isDefaultAvatar () {
        return this.avatarImageData === DEFAULT_AVATAR_URL
      }
    },

    data () {
      return {
        files: {
          avatar: null,
          top: null
        },
        topImageData: '',
        bottomImageData: '',
        avatarImageData: '',
        topImageSrc: '',
        bottomImageSrc: '',
        avatarImageSrc: '',
        editMode: false,
        imageData: null,
        previewCardScale: 1.0,
        previewCardHeight: 630
      }
    },

    created () {
      this.editMode = this.editing

      this.topImageSrc = this.topImage
      if (!this.topImageSrc) {
        this.updateTopImage()
      }

      this.bottomImageSrc = this.bottomImage
      if (!this.bottomImageSrc) {
        this.updateBottomImage()
      }

      this.avatarImageSrc = this.avatarImage
      if (!this.avatarImageSrc) {
        this.avatarImageSrc = DEFAULT_AVATAR_URL
      }
    },

    mounted () {
      this.preloadImage('topImageSrc', 'topImageData')
      this.preloadImage('bottomImageSrc', 'bottomImageData')
      this.preloadImage('avatarImageSrc', 'avatarImageData')

      this.calculateCardSize()
      window.addEventListener('resize', this.handleResize)
    },

    beforeDestroy: function () {
      window.removeEventListener('resize', this.handleResize)
    },

    methods: {
      shuffle (a) {
        var j, x, i
        for (i = a.length - 1; i > 0; i--) {
          j = Math.floor(Math.random() * (i + 1))
          x = a[i]
          a[i] = a[j]
          a[j] = x
        }
      },

      updateTopImage () {
        const topList = ['01', '02']
        this.shuffle(topList)
        this.topImageSrc = `/static/img/image-preset-event/${topList[0]}.jpg`
      },

      updateBottomImage () {
        if (this.type === this.$const.SPECIAL_EVENT_TYPES.OTHER) {
          const bottomList = ['03', '04', '05']
          this.shuffle(bottomList)
          this.bottomImageSrc = `/static/img/image-preset-event/preset-${bottomList[0]}.jpg`
        } else {
          this.bottomImageSrc = `/static/img/image-preset-event/preset-0${this.type}.jpg`
        }
      },

      preloadImage (src, dst) {
        if (this[src].startsWith('http')) {
          const canvas = document.createElement('canvas')
          document.body.appendChild(canvas)
          const ctx = canvas.getContext('2d')
          const im = new window.Image()
          im.setAttribute('crossOrigin', 'anonymous')
          im.onload = () => {
            ctx.canvas.width = im.width
            ctx.canvas.height = im.height
            ctx.drawImage(im, 0, 0)
            this[dst] = canvas.toDataURL()
            canvas.parentNode.removeChild(canvas)
          }
          im.src = this[src]
        } else {
          this[dst] = this[src]
        }
      },

      handleResize () {
        if (this.resizeTimer) {
          clearTimeout(this.resizeTimer)
          this.resizeTimer = null
        }

        this.resizeTimer = setTimeout(() => {
          this.calculateCardSize()
        }, 500)
      },

      calculateCardSize () {
        this.previewCardScale = this.$el.offsetWidth / 1200
        this.previewCardHeight = Math.floor(630 * this.previewCardScale)
      },

      onSelectFile (key, el) {
        const files = el.files
        if (!files || files.length === 0) {
          return
        }
        const file = files[0]

        if (!file.type.startsWith('image/')) {
          this.files[key] = null
          el.value = ''
          this.$modal.show({
            title: 'เกิดข้อผิดพลาด',
            text: 'อัพโหลดได้เฉพาะไฟล์รูปภาพเท่านั้น'
          })
          return
        }

        if (file.size > UPLOAD_MAX_SIZE) {
          this.value = ''
          this.$modal.show({
            title: 'เกิดข้อผิดพลาด',
            text: 'ขนากไฟล์ใหญ่เกินไป ไฟล์จะต้องไม่เกิน ' + window.Math.floor(UPLOAD_MAX_SIZE / 1000000) + 'MB'
          })
          return
        }

        this.files[key] = file

        const reader = new window.FileReader()
        reader.addEventListener('load', (e) => {
          if (key === 'avatar') {
            this.avatarImageData = reader.result
          } else {
            this.topImageData = reader.result
          }
          this.$emit('select-file', { key: key, file: this.files[key] }, reader.result)
        }, false)
        reader.readAsDataURL(this.files[key])
      },

      removeTopImage () {
        this.updateTopImage()
        this.preloadImage('topImageSrc', 'topImageData')
        this.files.top = null
      },

      captureCanvasToS3 () {
        // Sync image
        let result = {
          bottomImage: this.bottomImageSrc,
          topImage: this.topImageSrc,
          image: this.avatarImageSrc
        }
        let aclPublic = true

        const promises = []
        if (this.files.avatar) {
          const params = {
            mimetype: this.files.avatar.type,
            prefix: 'page-avatar',
            public: aclPublic
          }
          promises.push(this.$http.get('storage/presigned-url', { params: params }).then(resp => {
            const presignedUrl = resp.body.presignedUrl
            const viewUrl = resp.body.url
            this.uploadToS3(this.files.avatar, this.files.avatar.type, presignedUrl, viewUrl, aclPublic)
            result.image = viewUrl
            return Promise.resolve()
          }))
        } else {
          result.image = this.avatarImage
        }

        if (this.files.top) {
          const params = {
            mimetype: this.files.top.type,
            prefix: 'page-bg',
            public: aclPublic
          }
          promises.push(this.$http.get('storage/presigned-url', { params: params }).then(resp => {
            const presignedUrl = resp.body.presignedUrl
            const viewUrl = resp.body.url
            this.uploadToS3(this.files.top, this.files.top.type, presignedUrl, viewUrl, aclPublic)
            result.topImage = viewUrl
            return Promise.resolve()
          }))
        }

        // Sync share image
        promises.push(new Promise((resolve) => {
          // this.$refs.capturePageCard.capture()
          let mimetype = 'image/jpeg'

          html2canvas(this.$refs.captureCard, { scale: 1 }).then(canvas => {
            console.log('canvas', canvas.width, canvas.height)
            // this.$el.$ref.captureCanvas.appendChild(canvas)
            return pica.toBlob(canvas, mimetype)
          }).then(blob => {
            let file = null
            try {
              file = new window.File([blob], '', { type: mimetype })
            } catch (e) {
              file = new window.Blob([blob], { type: mimetype })
            }

            const params = {
              mimetype: mimetype,
              prefix: 'page-cover',
              public: aclPublic
            }
            this.$http.get('storage/presigned-url', { params: params }).then(resp => {
              const presignedUrl = resp.body.presignedUrl
              const viewUrl = resp.body.url
              this.uploadToS3(file, file.type, presignedUrl, viewUrl, aclPublic)
              result.shareImage = viewUrl
              resolve()
            })
          })
        }))

        return Promise.all(promises).then(() => {
          return result
        })
      },

      uploadToS3 (file, mimetype, presignedUrl, viewUrl, aclPublic) {
        let retryLimit = UPLOAD_RETRY_LIMIT

        let startUpload = () => {
          retryLimit--

          const oReq = new window.XMLHttpRequest()
          oReq.addEventListener('load', ev => {
            if (ev.target.status === 200) {
              this.$emit('uploaded', viewUrl.replace(/\?.*/, ''))
            } else {
              if (retryLimit > 0) {
                setTimeout(() => {
                  startUpload()
                }, UPLOAD_RETRY_WAIT)
              } else {
                // this.errorHandler(oReq) # crosalot said unnecessary
              }
            }
          })
          oReq.addEventListener('error', ev => {
            if (retryLimit > 0) {
              setTimeout(() => {
                startUpload()
              }, UPLOAD_RETRY_WAIT)
            } else {
              // this.errorHandler(oReq) # crosalot said unnecessary
            }
          })

          oReq.open('PUT', presignedUrl, true)
          oReq.setRequestHeader('Content-Type', mimetype)
          if (aclPublic) {
            oReq.setRequestHeader('X-Amz-Acl', 'public-read')
          }
          oReq.send(file)
        }

        startUpload()
      },

      edit () {
        this.editMode = true
        this.previousTopImageSrc = this.topImageSrc
        this.previousTopImageData = this.topImageData
        this.$emit('edit')
      },

      cancel () {
        this.editMode = false
        if (this.previousTopImageSrc) {
          this.topImageSrc = this.previousTopImageSrc
          this.previousTopImageSrc = null
        }
        if (this.previousTopImageData) {
          this.topImageData = this.previousTopImageData
          this.previousTopImageData = null
        }
        this.$emit('cancel')
      }
    }
  }
</script>

<style lang="scss" scoped>

  .has-avatar {
    .form__file {
      opacity: 1;
      background: none;
      &:before {
        position: absolute;
        bottom: -10px;
        right: -10px;
        content: " ";
        width: 55px;
        height: 55px;
        background: url("../../../../assets/img/ic-camera@2x.png") no-repeat center center;
        background-size: 100%;
        top: auto;
        left: auto;
        transition: inherit;
      }
      &:hover {
        opacity: 0.9;
      }
    }
  }

  .form__file {
    position: absolute;
    top: calc(50% + 2px);
    left: 50%;
    transform: translate(-50%,-50%);
    width: 250px;
    height: 250px;
    background: url("../../../../assets/img/upload-file@2x.png") no-repeat center center;
    background-size: 100%;
    opacity: 1;
    &:before {
      content: "อัพโหลดรูป";
      top: 80%;
      left: 50%;
      transform: translate(-50%,-80%);
      position: absolute;
      font-size: 1.2em;
      font-weight: 400;
    }
    input {
      opacity: 0;
      width: 100%;
      height: 100%;
      cursor: pointer;
    }
  }

  .form__file--bg {
    position: absolute;
    top: 15px;
    left: 15px;
    width: 150px;
    height: 45px;
    background: url("../../../../assets/img/btn-chang_bg@2x.png") no-repeat center center;
    background-size: 150px;
    opacity: 1;

    input {
      opacity: 0;
      width: 100%;
      height: 100%;
      cursor: pointer;
    }
  }

  .remove-file {
    position: absolute;
    top: 64px;
    left: 15px;
    color: black;
    background: #fff;
    padding: 5px 10px;
    border-radius: 5px;
  }


  .wrapper-preview-container {
    position: relative;
    margin-bottom: 0.75em;

    .wrapper-preview {
      width: 1200px;
      transform-origin: top left;

    }

    .img-circle {
      position: absolute;
      top: 48%;
      left: 50%;
      transform: translate(-50%,-50%);
      width: 200px;
      height: 200px;
      border-radius: 50%;
      border: 10px solid #fff;
      margin-bottom: 1em;
      box-sizing: border-box;
    }
  }

  .capture-image-wrapper {
    width: 0;
    height: 0;
    overflow: hidden;
  }

  .capture-image {
    overflow: hidden;
    height: 630px;
    width: 1200px;
  }

  .preview-edit {
    .pull-right {
      font-size: 0.75em;
      position: absolute;
      top: 10px;
      right: 10px;
      padding: 2px 6px;
      background: #fff;
      border-radius: 5px;
    }
  }

</style>



// WEBPACK FOOTER //
// card.vue?6fe381ab